import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowLeft, Loader2, ShieldAlert, FileText, FileSignature, ListMusic, Music2, CreditCard, FolderOpen, Download, Upload, Trash2, Images, Star, BookOpen, Pencil } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { GoogleLoginButton } from "@/lib/auth";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUTS = {
  nouvelle: { label: "Nouvelle", cls: "border-cyan-400/30 bg-cyan-400/10 text-cyan-300" },
  en_discussion: { label: "En discussion", cls: "border-amber-400/30 bg-amber-400/10 text-amber-300" },
  acceptee: { label: "Acceptée", cls: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" },
  refusee: { label: "Refusée", cls: "border-red-400/30 bg-red-400/10 text-red-300" },
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#080C14]">
    <header className="sticky top-0 z-40 border-b border-cyan-500/15 bg-[#080C14]/85 backdrop-blur-xl">
      <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          data-testid="admin-back-link"
          className="group flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"
        >
          <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Retour au site
        </Link>
        <img src="/logo-v2.png" alt="KLMProd" className="h-8 w-auto" />
      </div>
    </header>
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">{children}</div>
  </div>
);

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [devis, setDevis] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [payments, setPayments] = useState([]);
  const [files, setFiles] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [contrats, setContrats] = useState([]);
  const [avis, setAvis] = useState([]);
  const [avisForm, setAvisForm] = useState({ nom: "", evenement: "", texte: "", note: 5, source: "" });
  const [avisSaving, setAvisSaving] = useState(false);

  const addAvis = async () => {
    setAvisSaving(true);
    try {
      await axios.post(`${API}/avis`, { ...avisForm, note: Number(avisForm.note) });
      setAvisForm({ nom: "", evenement: "", texte: "", note: 5, source: "" });
      const { data } = await axios.get(`${API}/avis`);
      setAvis(data);
      toast.success("Avis publié sur le site");
    } catch {
      toast.error("Impossible d'ajouter l'avis", { description: "Vérifiez les champs (texte de 10 caractères minimum)." });
    } finally {
      setAvisSaving(false);
    }
  };

  const deleteAvis = async (id) => {
    try {
      await axios.delete(`${API}/avis/${id}`);
      setAvis((a) => a.filter((x) => x.id !== id));
    } catch {}
  };

  const publierAvis = async (id) => {
    try {
      await axios.post(`${API}/avis/${id}/publier`);
      setAvis((a) => a.map((x) => (x.id === id ? { ...x, publie: true } : x)));
      toast.success("Avis publié sur le site");
    } catch {
      toast.error("Publication impossible");
    }
  };

  const [editingDevis, setEditingDevis] = useState(null);
  const [devisForm, setDevisForm] = useState({});
  const [payForm, setPayForm] = useState({ montant: "", libelle: "acompte", note: "" });
  const [paySaving, setPaySaving] = useState(false);

  const saveDevis = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/devis/${id}`, patch);
      const { data } = await axios.get(`${API}/admin/devis`);
      setDevis(data);
      setEditingDevis(null);
      toast.success("Demande mise à jour");
    } catch {
      toast.error("Modification impossible");
    }
  };

  const deleteDevis = async (id) => {
    try {
      await axios.delete(`${API}/admin/devis/${id}`);
      setDevis((d) => d.filter((x) => x.id !== id));
      toast.success("Demande supprimée");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const addManualPayment = async () => {
    setPaySaving(true);
    try {
      await axios.post(`${API}/admin/payments`, { ...payForm, montant: Number(payForm.montant) });
      setPayForm({ montant: "", libelle: "acompte", note: "" });
      const { data } = await axios.get(`${API}/admin/payments`);
      setPayments(data);
      toast.success("Paiement hors ligne ajouté");
    } catch {
      toast.error("Ajout impossible", { description: "Vérifiez le montant." });
    } finally {
      setPaySaving(false);
    }
  };

  const markPaidOffline = async (sessionId) => {
    try {
      await axios.patch(`${API}/admin/payments/${sessionId}`, { marque_paye: true });
      setPayments((p) => p.map((x) => (x.session_id === sessionId ? { ...x, payment_status: "paid", methode: "hors_ligne" } : x)));
      toast.success("Marqué comme payé (hors ligne)");
    } catch {
      toast.error("Modification impossible");
    }
  };

  const deletePayment = async (sessionId) => {
    try {
      await axios.delete(`${API}/admin/payments/${sessionId}`);
      setPayments((p) => p.filter((x) => x.session_id !== sessionId));
      toast.success("Paiement supprimé");
    } catch (e) {
      toast.error("Suppression impossible", { description: e.response?.data?.detail || "" });
    }
  };

  const deleteContrat = async (id) => {
    try {
      await axios.delete(`${API}/admin/contrats/${id}`);
      setContrats((c) => c.filter((x) => x.id !== id));
      toast.success("Contrat supprimé");
    } catch (e) {
      toast.error("Suppression impossible", { description: e.response?.data?.detail || "" });
    }
  };
  const [galleryCaption, setGalleryCaption] = useState("");
  const [galleryUploading, setGalleryUploading] = useState(false);
  const galleryInputRef = useRef(null);

  const uploadGalleryImage = async () => {
    const f = galleryInputRef.current?.files?.[0];
    if (!f) return;
    setGalleryUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("caption", galleryCaption);
      await axios.post(`${API}/gallery`, fd);
      galleryInputRef.current.value = "";
      setGalleryCaption("");
      const { data } = await axios.get(`${API}/gallery`);
      setGallery(data);
    } catch {} finally {
      setGalleryUploading(false);
    }
  };

  const deleteGalleryImage = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`);
      setGallery((g) => g.filter((x) => x.id !== id));
    } catch {}
  };
  const [fetching, setFetching] = useState(true);

  const downloadFile = async (f) => {
    try {
      const res = await axios.get(`${API}/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  useEffect(() => {
    if (loading) return;
    if (!user?.is_admin) {
      setFetching(false);
      return;
    }
    Promise.all([
      axios.get(`${API}/admin/devis`),
      axios.get(`${API}/admin/playlists`),
      axios.get(`${API}/admin/payments`),
      axios.get(`${API}/uploads`),
      axios.get(`${API}/gallery`),
      axios.get(`${API}/admin/contrats`),
      axios.get(`${API}/admin/avis`),
    ])
      .then(([d, p, pay, f, g, c, a]) => {
        setDevis(d.data);
        setPlaylists(p.data);
        setPayments(pay.data);
        setFiles(f.data);
        setGallery(g.data);
        setContrats(c.data);
        setAvis(a.data);
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [user, loading]);

  if (loading || (user?.is_admin && fetching)) {
    return (
      <Shell>
        <div className="flex h-80 items-center justify-center" data-testid="admin-loading">
          <Loader2 size={26} className="animate-spin text-cyan-400" />
        </div>
      </Shell>
    );
  }

  if (!user) {
    return (
      <Shell>
        <div className="mx-auto flex max-w-md flex-col items-center pt-16 text-center" data-testid="admin-login-gate">
          <ListMusic size={36} className="text-cyan-400" />
          <h1 className="font-display mt-5 text-2xl font-extrabold text-slate-50">Espace administrateur</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-400">
            Connectez-vous avec votre compte Google professionnel pour consulter les demandes de
            devis et gérer les playlists.
          </p>
          <GoogleLoginButton testId="admin-login-button" className="mt-7" />
        </div>
      </Shell>
    );
  }

  if (!user.is_admin) {
    return (
      <Shell>
        <div className="mx-auto flex max-w-md flex-col items-center pt-16 text-center" data-testid="admin-forbidden">
          <ShieldAlert size={36} className="text-amber-400" />
          <h1 className="font-display mt-5 text-2xl font-extrabold text-slate-50">Accès réservé</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-400">
            Ce compte ({user.email}) n'a pas les droits d'administration. Contactez KLMProd si vous
            pensez qu'il s'agit d'une erreur.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div data-testid="admin-dashboard">
        <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-cyan-400">Espace administrateur</p>
        <h1 className="font-display mt-3 text-3xl font-extrabold text-slate-50 sm:text-4xl">
          Bonjour {user.name?.split(" ")[0]}.
        </h1>
        <Link
          to="/admin/guide"
          data-testid="admin-guide-link"
          className="mt-4 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 px-5 py-2.5 text-xs font-semibold text-cyan-300 transition-colors hover:bg-cyan-400/10"
        >
          <BookOpen size={14} /> Guide d'utilisation
        </Link>

        <section className="mt-12" data-testid="admin-devis-section">
          <h2 className="font-display flex items-center gap-2.5 text-xl font-bold text-slate-100">
            <FileText size={19} className="text-cyan-400" /> Demandes de devis ({devis.length})
          </h2>
          <div className="mt-5 space-y-3">
            {devis.length === 0 && (
              <p className="rounded-2xl border border-cyan-500/10 bg-[#0E1524]/60 p-6 text-sm text-slate-500" data-testid="admin-devis-empty">
                Aucune demande de devis pour le moment.
              </p>
            )}
            {devis.map((d) => (
              <div key={d.id} className="rounded-2xl border border-cyan-500/15 bg-[#0E1524]/70 p-5" data-testid={`admin-devis-${d.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-slate-100">{d.nom}</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {d.email}
                      {d.telephone ? ` · ${d.telephone}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-[11px] font-bold text-cyan-300">
                      {d.formule} · {d.invites} invités
                    </span>
                    <select
                      value={d.statut || "nouvelle"}
                      onChange={(e) => saveDevis(d.id, { statut: e.target.value })}
                      data-testid={`devis-statut-${d.id}`}
                      className={`cursor-pointer rounded-full border px-3 py-1 text-[11px] font-bold ${STATUTS[d.statut || "nouvelle"].cls}`}
                    >
                      {Object.entries(STATUTS).map(([k, s]) => (
                        <option key={k} value={k} className="bg-[#0E1524] text-slate-100">{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="mt-2.5 text-xs text-slate-400">
                  {d.type_evenement}
                  {d.date_evenement ? ` · le ${format(parseISO(d.date_evenement), "d MMMM yyyy", { locale: fr })}` : ""}
                  {" · reçue le "}
                  {format(parseISO(d.created_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                  {d.montant_convenu ? ` · montant convenu ${d.montant_convenu.toLocaleString("fr-FR")} €` : ""}
                </p>
                {d.message && (
                  <p className="mt-2 rounded-xl bg-[#080C14]/60 p-3 text-xs leading-relaxed text-slate-400">« {d.message} »</p>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    data-testid={`devis-contract-${d.id}`}
                    onClick={async () => {
                      try {
                        const { data } = await axios.post(`${API}/admin/contrats`, { devis_id: d.id });
                        const lien = `${window.location.origin}/signature/${data.token}`;
                        try {
                          await navigator.clipboard.writeText(lien);
                        } catch {}
                        toast.success("Lien de signature copié", { description: lien });
                        const rc = await axios.get(`${API}/admin/contrats`);
                        setContrats(rc.data);
                      } catch {
                        toast.error("Création du contrat impossible");
                      }
                    }}
                    className="flex items-center gap-1.5 rounded-full border border-cyan-400/30 px-4 py-2 text-xs font-semibold text-cyan-300 transition-colors hover:bg-cyan-400/10"
                  >
                    <FileSignature size={13} /> Lien de signature
                  </button>
                  <button
                    data-testid={`devis-edit-${d.id}`}
                    onClick={() => {
                      setEditingDevis(editingDevis === d.id ? null : d.id);
                      setDevisForm({
                        date_evenement: d.date_evenement || "",
                        formule: d.formule,
                        invites: d.invites,
                        telephone: d.telephone || "",
                        montant_convenu: d.montant_convenu ?? "",
                      });
                    }}
                    className="flex items-center gap-1.5 rounded-full border border-slate-500/30 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:bg-slate-400/10"
                  >
                    <Pencil size={13} /> Modifier
                  </button>
                  <button
                    data-testid={`devis-delete-${d.id}`}
                    onClick={() => deleteDevis(d.id)}
                    className="flex items-center gap-1.5 rounded-full border border-red-400/30 px-4 py-2 text-xs font-semibold text-red-300 transition-colors hover:bg-red-400/10"
                  >
                    <Trash2 size={13} /> Supprimer
                  </button>
                </div>
                {editingDevis === d.id && (
                  <div className="mt-4 grid grid-cols-1 gap-3 rounded-xl border border-cyan-500/15 bg-[#080C14]/60 p-4 sm:grid-cols-2" data-testid={`devis-edit-form-${d.id}`}>
                    <label className="text-xs text-slate-400">
                      Date de l'événement
                      <input
                        type="date"
                        value={devisForm.date_evenement}
                        onChange={(e) => setDevisForm((f) => ({ ...f, date_evenement: e.target.value }))}
                        data-testid={`devis-edit-date-${d.id}`}
                        className="mt-1 w-full rounded-md border border-cyan-500/20 bg-[#080C14]/70 px-3 py-2 text-sm text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
                      />
                    </label>
                    <label className="text-xs text-slate-400">
                      Formule
                      <select
                        value={devisForm.formule}
                        onChange={(e) => setDevisForm((f) => ({ ...f, formule: e.target.value }))}
                        data-testid={`devis-edit-formule-${d.id}`}
                        className="mt-1 w-full rounded-md border border-cyan-500/20 bg-[#080C14]/70 px-3 py-2 text-sm text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
                      >
                        {["Essentielle", "Médium", "Prestige"].map((f) => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs text-slate-400">
                      Nombre d'invités
                      <input
                        type="number"
                        min={20}
                        max={300}
                        value={devisForm.invites}
                        onChange={(e) => setDevisForm((f) => ({ ...f, invites: Number(e.target.value) }))}
                        data-testid={`devis-edit-invites-${d.id}`}
                        className="mt-1 w-full rounded-md border border-cyan-500/20 bg-[#080C14]/70 px-3 py-2 text-sm text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
                      />
                    </label>
                    <label className="text-xs text-slate-400">
                      Téléphone
                      <input
                        value={devisForm.telephone}
                        onChange={(e) => setDevisForm((f) => ({ ...f, telephone: e.target.value }))}
                        data-testid={`devis-edit-telephone-${d.id}`}
                        className="mt-1 w-full rounded-md border border-cyan-500/20 bg-[#080C14]/70 px-3 py-2 text-sm text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
                      />
                    </label>
                    <label className="text-xs text-slate-400 sm:col-span-2">
                      Montant convenu (€) — prix final négocié, options comprises
                      <input
                        type="number"
                        min={0}
                        value={devisForm.montant_convenu}
                        onChange={(e) => setDevisForm((f) => ({ ...f, montant_convenu: e.target.value }))}
                        placeholder="Ex. 1350"
                        data-testid={`devis-edit-montant-${d.id}`}
                        className="mt-1 w-full rounded-md border border-cyan-500/20 bg-[#080C14]/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
                      />
                    </label>
                    <div className="flex gap-2 sm:col-span-2">
                      <button
                        data-testid={`devis-edit-save-${d.id}`}
                        onClick={() =>
                          saveDevis(d.id, {
                            date_evenement: devisForm.date_evenement || null,
                            formule: devisForm.formule,
                            invites: Number(devisForm.invites),
                            telephone: devisForm.telephone || null,
                            montant_convenu: devisForm.montant_convenu === "" ? null : Number(devisForm.montant_convenu),
                          })
                        }
                        className="bg-brand-gradient flex items-center gap-1.5 rounded-full px-5 py-2 text-xs font-bold text-[#04121f] transition-transform duration-300 hover:scale-[1.03]"
                      >
                        Enregistrer
                      </button>
                      <button
                        data-testid={`devis-edit-cancel-${d.id}`}
                        onClick={() => setEditingDevis(null)}
                        className="rounded-full border border-slate-500/30 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:bg-slate-400/10"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14" data-testid="admin-playlists-section">
          <h2 className="font-display flex items-center gap-2.5 text-xl font-bold text-slate-100">
            <ListMusic size={19} className="text-cyan-400" /> Playlists ({playlists.length})
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {playlists.length === 0 && (
              <p className="rounded-2xl border border-cyan-500/10 bg-[#0E1524]/60 p-6 text-sm text-slate-500" data-testid="admin-playlists-empty">
                Aucune playlist créée.
              </p>
            )}
            {playlists.map((p) => (
              <div key={p.id} className="rounded-2xl border border-cyan-500/15 bg-[#0E1524]/70 p-5" data-testid={`admin-playlist-${p.id}`}>
                <p className="text-sm font-bold text-slate-100">{p.titre}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {format(parseISO(p.date_evenement), "d MMMM yyyy", { locale: fr })} · {p.type_evenement}
                </p>
                <p className="mt-2 flex items-center gap-1.5 text-xs text-cyan-300">
                  <Music2 size={12} /> {p.tracks_count} titre{p.tracks_count > 1 ? "s" : ""}
                  {p.verrouillee && <span className="ml-2 text-amber-300">· verrouillée</span>}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14" data-testid="admin-payments-section">
          <h2 className="font-display flex items-center gap-2.5 text-xl font-bold text-slate-100">
            <CreditCard size={19} className="text-cyan-400" /> Paiements ({payments.length})
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-3 rounded-2xl border border-cyan-500/15 bg-[#0E1524]/70 p-5 sm:grid-cols-4" data-testid="admin-payment-add-form">
            <Input
              type="number"
              min={0}
              value={payForm.montant}
              onChange={(e) => setPayForm((f) => ({ ...f, montant: e.target.value }))}
              placeholder="Montant (€)"
              data-testid="admin-payment-montant"
              className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
            />
            <select
              value={payForm.libelle}
              onChange={(e) => setPayForm((f) => ({ ...f, libelle: e.target.value }))}
              data-testid="admin-payment-libelle"
              className="rounded-md border border-cyan-500/20 bg-[#080C14]/70 px-3 py-2 text-sm text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
            >
              <option value="acompte">Acompte</option>
              <option value="solde">Solde</option>
              <option value="autre">Autre</option>
            </select>
            <Input
              value={payForm.note}
              onChange={(e) => setPayForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="Note (ex. chèque reçu, virement)"
              data-testid="admin-payment-note"
              className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
            />
            <button
              onClick={addManualPayment}
              disabled={paySaving || !payForm.montant || Number(payForm.montant) <= 0}
              data-testid="admin-payment-add-button"
              className="bg-brand-gradient flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold text-[#04121f] transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60"
            >
              {paySaving ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
              Ajouter un règlement hors ligne
            </button>
          </div>
          <div className="mt-5 space-y-3">
            {payments.length === 0 && (
              <p className="rounded-2xl border border-cyan-500/10 bg-[#0E1524]/60 p-6 text-sm text-slate-500" data-testid="admin-payments-empty">
                Aucun paiement pour le moment.
              </p>
            )}
            {payments.map((p) => (
              <div
                key={p.session_id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-cyan-500/15 bg-[#0E1524]/70 p-5"
                data-testid={`admin-payment-${p.session_id}`}
              >
                <div>
                  <p className="text-sm font-bold text-slate-100">
                    {(p.amount / 100).toLocaleString("fr-FR")} €{" "}
                    <span className="font-normal capitalize text-slate-500">
                      — {(p.lookup_key || "").replace("acompte_", "acompte ")}
                    </span>
                    {p.methode === "hors_ligne" && (
                      <span className="ml-2 rounded-full border border-violet-400/30 bg-violet-400/10 px-2.5 py-0.5 text-[10px] font-bold text-violet-300">
                        hors ligne
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {format(parseISO(p.created_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                    {p.note ? ` · ${p.note}` : ""}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-3 py-1 text-[11px] font-bold ${
                      p.payment_status === "paid"
                        ? "border border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                        : "border border-amber-400/30 bg-amber-400/10 text-amber-300"
                    }`}
                    data-testid={`admin-payment-status-${p.session_id}`}
                  >
                    {p.payment_status === "paid" ? "Payé" : p.payment_status}
                  </span>
                  {p.payment_status !== "paid" && (
                    <button
                      onClick={() => markPaidOffline(p.session_id)}
                      data-testid={`admin-payment-paid-${p.session_id}`}
                      className="rounded-full border border-emerald-400/30 px-3.5 py-1.5 text-[11px] font-semibold text-emerald-300 transition-colors hover:bg-emerald-400/10"
                    >
                      Marquer payé (hors ligne)
                    </button>
                  )}
                  {(p.session_id.startsWith("manuel-") || p.payment_status !== "paid") && (
                    <button
                      onClick={() => deletePayment(p.session_id)}
                      data-testid={`admin-payment-delete-${p.session_id}`}
                      className="text-slate-400 transition-colors hover:text-red-300"
                      aria-label="Supprimer"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14" data-testid="admin-files-section">
          <h2 className="font-display flex items-center gap-2.5 text-xl font-bold text-slate-100">
            <FolderOpen size={19} className="text-cyan-400" /> Fichiers clients ({files.length})
          </h2>
          <div className="mt-5 space-y-3">
            {files.length === 0 && (
              <p className="rounded-2xl border border-cyan-500/10 bg-[#0E1524]/60 p-6 text-sm text-slate-500" data-testid="admin-files-empty">
                Aucun fichier envoyé pour le moment.
              </p>
            )}
            {files.map((f) => (
              <div
                key={f.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-cyan-500/15 bg-[#0E1524]/70 p-5"
                data-testid={`admin-file-${f.id}`}
              >
                <div>
                  <p className="text-sm font-bold text-slate-100">{f.original_filename}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {f.user_name} ({f.user_email}) · {f.categorie} · {(f.size / 1024 / 1024).toFixed(1)} Mo ·{" "}
                    {format(parseISO(f.created_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                  </p>
                </div>
                <button
                  onClick={() => downloadFile(f)}
                  data-testid={`admin-file-download-${f.id}`}
                  className="flex items-center gap-1.5 rounded-full border border-cyan-400/30 px-4 py-2 text-xs font-semibold text-cyan-300 transition-colors hover:bg-cyan-400/10"
                >
                  <Download size={13} /> Télécharger
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14" data-testid="admin-gallery-section">
          <h2 className="font-display flex items-center gap-2.5 text-xl font-bold text-slate-100">
            <Images size={19} className="text-cyan-400" /> Galerie publique ({gallery.length})
          </h2>
          <p className="mt-2 text-xs text-slate-500">
            Les photos ajoutées ici remplacent les images d'exemple de la section « Galerie » du site.
          </p>
          <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-cyan-500/15 bg-[#0E1524]/70 p-5 sm:flex-row">
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              data-testid="admin-gallery-input"
              className="flex-1 self-center text-xs text-slate-400 file:mr-3 file:rounded-full file:border-0 file:bg-cyan-400/10 file:px-4 file:py-2 file:text-xs file:font-semibold file:text-cyan-300 hover:file:bg-cyan-400/20"
            />
            <Input
              value={galleryCaption}
              onChange={(e) => setGalleryCaption(e.target.value)}
              placeholder="Légende (ex. Mariage Juillet 2026)"
              data-testid="admin-gallery-caption"
              className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50 sm:w-64"
            />
            <button
              onClick={uploadGalleryImage}
              disabled={galleryUploading}
              data-testid="admin-gallery-upload-button"
              className="bg-brand-gradient flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold text-[#04121f] transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60"
            >
              {galleryUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Ajouter
            </button>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="admin-gallery-list">
            {gallery.length === 0 && (
              <p className="col-span-full rounded-2xl border border-cyan-500/10 bg-[#0E1524]/60 p-6 text-sm text-slate-500" data-testid="admin-gallery-empty">
                Aucune photo pour le moment — la galerie affiche des images d'exemple.
              </p>
            )}
            {gallery.map((g) => (
              <div key={g.id} className="group relative overflow-hidden rounded-2xl border border-cyan-500/15" data-testid={`admin-gallery-item-${g.id}`}>
                <img src={`${API}/gallery/${g.id}/image`} alt={g.caption} className="aspect-square w-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-[#080C14]/85 px-3 py-2 backdrop-blur">
                  <span className="truncate text-[11px] text-slate-300">{g.caption || g.original_filename}</span>
                  <button
                    onClick={() => deleteGalleryImage(g.id)}
                    data-testid={`admin-gallery-delete-${g.id}`}
                    className="shrink-0 text-slate-400 transition-colors hover:text-red-300"
                    aria-label="Supprimer"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14" data-testid="admin-avis-section">
          <h2 className="font-display flex items-center gap-2.5 text-xl font-bold text-slate-100">
            <Star size={19} className="text-cyan-400" /> Avis clients ({avis.length})
          </h2>
          <p className="mt-2 text-xs text-slate-500">
            Les avis publiés s'affichent dans la section « Ils nous ont fait confiance » du site. Les avis envoyés par vos clients via le lien post-soirée arrivent ici « En attente » — publiez-les d'un clic après lecture.
          </p>
          <div className="mt-5 grid grid-cols-1 gap-3 rounded-2xl border border-cyan-500/15 bg-[#0E1524]/70 p-5 sm:grid-cols-2">
            <Input
              value={avisForm.nom}
              onChange={(e) => setAvisForm((f) => ({ ...f, nom: e.target.value }))}
              placeholder="Nom (ex. Sophie & Marc)"
              data-testid="admin-avis-nom"
              className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
            />
            <Input
              value={avisForm.evenement}
              onChange={(e) => setAvisForm((f) => ({ ...f, evenement: e.target.value }))}
              placeholder="Événement (ex. Mariage — Valence, juin 2026)"
              data-testid="admin-avis-evenement"
              className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
            />
            <textarea
              value={avisForm.texte}
              onChange={(e) => setAvisForm((f) => ({ ...f, texte: e.target.value }))}
              placeholder="Texte de l'avis…"
              rows={3}
              data-testid="admin-avis-texte"
              className="rounded-md border border-cyan-500/20 bg-[#080C14]/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50 sm:col-span-2"
            />
            <select
              value={avisForm.note}
              onChange={(e) => setAvisForm((f) => ({ ...f, note: e.target.value }))}
              data-testid="admin-avis-note"
              className="rounded-md border border-cyan-500/20 bg-[#080C14]/70 px-3 py-2 text-sm text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
            >
              {[5, 4, 3, 2, 1].map((n) => (
                <option key={n} value={n}>{n} étoile{n > 1 ? "s" : ""}</option>
              ))}
            </select>
            <select
              value={avisForm.source}
              onChange={(e) => setAvisForm((f) => ({ ...f, source: e.target.value }))}
              data-testid="admin-avis-source"
              className="rounded-md border border-cyan-500/20 bg-[#080C14]/70 px-3 py-2 text-sm text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50"
            >
              <option value="">Source : avis reçu directement</option>
              <option value="Google">Source : Google</option>
              <option value="mariages.net">Source : mariages.net</option>
            </select>
            <button
              onClick={addAvis}
              disabled={avisSaving || !avisForm.nom || avisForm.texte.length < 10}
              data-testid="admin-avis-add-button"
              className="bg-brand-gradient flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold text-[#04121f] transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60"
            >
              {avisSaving ? <Loader2 size={14} className="animate-spin" /> : <Star size={14} />}
              Publier l'avis
            </button>
          </div>
          <div className="mt-5 space-y-3">
            {avis.length === 0 && (
              <p className="rounded-2xl border border-cyan-500/10 bg-[#0E1524]/60 p-6 text-sm text-slate-500" data-testid="admin-avis-empty">
                Aucun avis pour le moment — la section avis est masquée sur le site tant qu'elle est vide.
              </p>
            )}
            {avis.map((a) => (
              <div
                key={a.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-cyan-500/15 bg-[#0E1524]/70 p-5"
                data-testid={`admin-avis-${a.id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-100">
                    {a.nom}
                    <span className="ml-2 font-normal text-amber-300">{"★".repeat(a.note)}</span>
                    {a.source && (
                      <span className="ml-2 text-[10px] font-normal text-cyan-300">via {a.source}</span>
                    )}
                    {!a.publie && (
                      <span className="ml-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-2.5 py-0.5 text-[10px] font-bold text-amber-300">
                        En attente
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">{a.evenement}</p>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-slate-400">« {a.texte} »</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {!a.publie && (
                    <button
                      onClick={() => publierAvis(a.id)}
                      data-testid={`admin-avis-publier-${a.id}`}
                      className="rounded-full border border-emerald-400/30 px-3.5 py-1.5 text-[11px] font-semibold text-emerald-300 transition-colors hover:bg-emerald-400/10"
                    >
                      Publier
                    </button>
                  )}
                  <button
                    onClick={() => deleteAvis(a.id)}
                    data-testid={`admin-avis-delete-${a.id}`}
                    className="text-slate-400 transition-colors hover:text-red-300"
                    aria-label="Supprimer"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14" data-testid="admin-contrats-section">
          <h2 className="font-display flex items-center gap-2.5 text-xl font-bold text-slate-100">
            <FileSignature size={19} className="text-cyan-400" /> Contrats ({contrats.length})
          </h2>
          <div className="mt-5 space-y-3">
            {contrats.length === 0 && (
              <p className="rounded-2xl border border-cyan-500/10 bg-[#0E1524]/60 p-6 text-sm text-slate-500" data-testid="admin-contrats-empty">
                Aucun contrat. Générez un lien de signature depuis une demande de devis ci-dessus.
              </p>
            )}
            {contrats.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-cyan-500/15 bg-[#0E1524]/70 p-5"
                data-testid={`admin-contrat-${c.id}`}
              >
                <div>
                  <p className="text-sm font-bold text-slate-100">
                    {c.client_nom} — Formule {c.formule}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {c.type_evenement}
                    {c.date_evenement ? ` · ${c.date_evenement}` : ""} · acompte{" "}
                    {c.acompte.toLocaleString("fr-FR")} €
                    {c.signataire ? ` · signé par ${c.signataire}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-3 py-1 text-[11px] font-bold ${
                      c.statut === "signe"
                        ? "border border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                        : "border border-amber-400/30 bg-amber-400/10 text-amber-300"
                    }`}
                    data-testid={`admin-contrat-status-${c.id}`}
                  >
                    {c.statut === "signe" ? "Signé" : "En attente"}
                  </span>
                  <button
                    data-testid={`admin-contrat-copy-${c.id}`}
                    onClick={async () => {
                      const lien = `${window.location.origin}/signature/${c.token}`;
                      try {
                        await navigator.clipboard.writeText(lien);
                      } catch {}
                      toast.success("Lien copié", { description: lien });
                    }}
                    className="rounded-full border border-cyan-400/30 px-3.5 py-1.5 text-[11px] font-semibold text-cyan-300 transition-colors hover:bg-cyan-400/10"
                  >
                    Copier le lien
                  </button>
                  <a
                    href={`${API}/contrats/${c.token}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`admin-contrat-pdf-${c.id}`}
                    className="rounded-full border border-cyan-400/30 px-3.5 py-1.5 text-[11px] font-semibold text-cyan-300 transition-colors hover:bg-cyan-400/10"
                  >
                    PDF
                  </a>
                  {c.statut !== "signe" ? (
                    <button
                      data-testid={`admin-contrat-delete-${c.id}`}
                      onClick={() => deleteContrat(c.id)}
                      className="rounded-full border border-red-400/30 px-3.5 py-1.5 text-[11px] font-semibold text-red-300 transition-colors hover:bg-red-400/10"
                    >
                      Supprimer
                    </button>
                  ) : (
                    <span
                      className="text-[10px] italic text-slate-600"
                      title="Un contrat signé est conservé comme archive légale"
                    >
                      archive
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Shell>
  );
}
